import { inject, Injectable, NgZone } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Observable, takeUntil } from 'rxjs';
import { RequestBetModel } from 'src/app/shared';
import { isStreamingCambioEventPayload } from 'src/app/shared/constants/streaming-sse.constants';
import { GameApiService } from 'src/app/shared/services/data/games/gameApi.service';
import { InfoApiService } from 'src/app/shared/services/data/games/infoApi.service';
import { StreamingConfigRuntimeService } from 'src/app/shared/services/streaming-config/streaming-config-runtime.service';
import { RequestResultModel } from 'src/app/shared/models/games/result.request';
import { environment } from 'src/environments/environment';
import { WSSseService } from './ws-sse-service';

@Injectable({ providedIn: 'root' })
export class SorteoSseHandlerService {
  private readonly sseService = inject(WSSseService);
  private readonly infoApiService = inject(InfoApiService);
  private readonly gameApiService = inject(GameApiService);
  private readonly toast = inject(ToastrService);
  private readonly ngZone = inject(NgZone);
  private readonly streamingConfigRuntime = inject(StreamingConfigRuntimeService);

  connect(destroy$: Observable<void>): void {
    this.sseService.messages$.pipe(takeUntil(destroy$)).subscribe({
      next: (data) => {
        if (!data || data.trim() === '') {
          return;
        }
        console.log('Datos recibidos de SSE:', data);
        try {
          if (this.tryHandleStreamingCambioSse(data)) {
            return;
          }
          this.handleGameTypeSse(data);
        } catch (error) {
          console.error('Error al analizar los datos de SSE:', error);
        }
      },
    });
  }

  /** info-api double-wraps SSE; extract JSON from raw frame when needed. */
  extractSseJsonCandidate(data: string): string {
    if (data.startsWith('{')) {
      return data;
    }
    const m = /^data:\s*(\{.+\})\s*$/m.exec(data);
    return m ? m[1] : data;
  }

  tryHandleStreamingCambioSse(data: string): boolean {
    try {
      const parsed: unknown = JSON.parse(this.extractSseJsonCandidate(data));
      if (!isStreamingCambioEventPayload(parsed)) {
        return false;
      }
      this.ngZone.run(() => this.streamingConfigRuntime.load());
      return true;
    } catch {
      return false;
    }
  }

  parseGameTypeFromSse(data: string): number | null {
    if (!/\{"gameType":\d+\}/.test(data)) {
      return null;
    }
    const gameTypeMatch = /\{"gameType":(\d+)\}/.exec(data);
    if (!gameTypeMatch) {
      return null;
    }
    return parseInt(gameTypeMatch[1], 10);
  }

  refreshInfoApiForGameType(gameTypeNumber: number): void {
    const requestFeatured: RequestResultModel = { page: 1 };
    const endpoints = this.infoApiService.endpoints.get;
    if (gameTypeNumber === 5) {
      endpoints.availableEventAndResultsGoldFive.signal.set(true);
    } else if (
      gameTypeNumber === environment.game.quinielaVesp.id ||
      gameTypeNumber === environment.game.quinielaNoct.id
    ) {
      // info-api always emits gameType=1 for any Quiniela result; refresh both home cards.
      endpoints.lastResultsByPageQuiniela.signal.set(requestFeatured);
      endpoints.lastResultsByPageQuinielaHomeComplement.signal.set(requestFeatured);
    } else if (gameTypeNumber === 3) {
      endpoints.lastResultsByPageTombola.signal.set(requestFeatured);
    } else if (gameTypeNumber === 4) {
      endpoints.lastResultsByPageTombolaHomeComplement.signal.set(requestFeatured);
    }
  }

  /**
   * Reloads game-page availableEvents from the app shell so any consumer of
   * GameApiService (banner, overlay, etc.) picks up the new sorteo without a full reload.
   */
  refreshGameApiForGameType(gameTypeNumber: number): void {
    if (gameTypeNumber !== environment.game.cincoDeOro.id) {
      return;
    }
    const request: RequestBetModel = { requestId: environment.game.cincoDeOro.id };
    const availableEvents = this.gameApiService.endpoints.get.availableEvents;
    // set triggers the first load; reload refreshes when the signal is already primed.
    if (availableEvents.signal() === undefined) {
      availableEvents.signal.set(request);
    } else {
      availableEvents.rxRes.reload();
    }
  }

  gameNameForType(gameTypeNumber: number): string | undefined {
    const gameNames: { [key: number]: string } = {
      [environment.game.quinielaVesp.id]: environment.game.quinielaVesp.name,
      [environment.game.quinielaNoct.id]: environment.game.quinielaNoct.name,
      [environment.game.tombolaVesp.id]: environment.game.tombolaVesp.name,
      [environment.game.tombolaNoct.id]: environment.game.tombolaNoct.name,
      [environment.game.cincoDeOro.id]: environment.game.cincoDeOro.name,
    };
    return gameNames[gameTypeNumber];
  }

  handleGameTypeSse(data: string): void {
    const gameTypeNumber = this.parseGameTypeFromSse(data);
    if (gameTypeNumber == null) {
      return;
    }
    console.log('Game type number extracted:', gameTypeNumber);
    this.ngZone.run(() => {
      this.refreshInfoApiForGameType(gameTypeNumber);
      this.refreshGameApiForGameType(gameTypeNumber);
      this.infoApiService.endpoints.get.datesForHistory.signal.set({ requestId: gameTypeNumber } as RequestBetModel);
      const name = this.gameNameForType(gameTypeNumber);
      this.toast.success(`Nuevos resultados disponibles para un sorteo de ${name}!`);
    });
  }
}
